import {first} from 'lodash';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {
  buildHoverParentClassName,
  directHoverSelector,
} from '../../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {reportToBugsnag} from '../../../../../../srcCommon/services/bugsnag/bugsnag';
import {VisualRecipientMap} from '../../../../../helpers/models/recipientHelpers';
import {MessageModel} from '../../../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../models/api/recipientModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {ContactCardLocationsEnum} from '../../../../../store/panels/contacts/contactCard/contactCardPanelState';
import {
  AccountDetailsPanelId,
  ContactDetailsPanelId,
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../store/panels/panelStateBase';
import {ContactAvatar} from '../../../../common/avatars/contactAvatar';
import {TeammateAvatar} from '../../../../common/avatars/teammateAvatar';
import {UnknownAvatar} from '../../../../common/avatars/unknownAvatar';
import {ContactCardWrapperContainer} from '../../../../contactCard/contactCardWrapperContainer';

/*
 * Props.
 */

// TODO: These typings should enforce either a teammate or recipient is provided (and visualRecipients should not be an
// empty object).
export interface MessageViewerAvatarProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | AccountDetailsPanelId | ContactDetailsPanelId;
  message: MessageModel;
  author?: TeammateModel;
  visualRecipients: VisualRecipientMap;
  isChatAvatar?: boolean;
  isChatUserOnline?: boolean;
}
interface StyledUserOnLineIndicatorDivProps {
  $isGreenCircle?: boolean;
}

/*
 * Constants.
 */

const avatarSize = VisualSizesEnum.LARGE;

/*
 * Style.
 */

const StyledUserOnLineIndicatorDiv = styled.div<StyledUserOnLineIndicatorDivProps>`
  ${(p) =>
    p.$isGreenCircle &&
    css`
      ${boxShadow(p.theme, `0 0 0 2px ${p.theme.palette.green.shade30}`)};
      border-radius: 50%;
      padding: 1px 1px;
    `}
`;

const StyledAvatarDiv = styled.div`
  position: relative;
`;

const StyledAvatarWrapper = styled.div`
  margin: 8px 7px;
  position: relative;
`;

const StyledAvatarHoverDetailsDiv = styled.div`
  pointer-events: none;
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  border-radius: 50%;

  ${directHoverSelector} {
    background: ${(p) => p.theme.alphas.black40};
  }
`;

/*
 * Component.
 */

export const MessageViewerAvatar: FC<MessageViewerAvatarProps> = (props) => {
  const {panelId, visualRecipients, author} = props;

  const fromRecipient = first(visualRecipients[RecipientRolesEnum.FROM]);
  if (!fromRecipient) {
    return (
      <StyledAvatarWrapper>
        <StyledAvatarDiv>{renderAvatar(props, fromRecipient)}</StyledAvatarDiv>
      </StyledAvatarWrapper>
    );
  }
  const {isChatAvatar, isChatUserOnline} = props;
  const isChatUserOnLineIndication = isChatAvatar && isChatUserOnline && !author;

  return (
    <TooltipCoordinator
      placement="top"
      customMargin={5}
      render={
        isChatUserOnLineIndication && (() => <Trans id="contact-avatar-chat-online-tooltip">Online</Trans>)
      }
    >
      <PopoverCoordinator
        renderButton={() => (
          <StyledAvatarWrapper>
            <StyledUserOnLineIndicatorDiv $isGreenCircle={isChatUserOnLineIndication}>
              <StyledAvatarDiv className={buildHoverParentClassName()}>
                {renderAvatar(props, fromRecipient)}
                <StyledAvatarHoverDetailsDiv />
              </StyledAvatarDiv>
            </StyledUserOnLineIndicatorDiv>
          </StyledAvatarWrapper>
        )}
        renderModal={(popoverProps) => renderContactDropdown(popoverProps, panelId, fromRecipient)}
      />
    </TooltipCoordinator>
  );
};

/*
 * Helpers.
 */

function renderAvatar(props: MessageViewerAvatarProps, fromRecipient?: VisualRecipientModel) {
  // If this is from a teammate, render the teammate avatar.
  const {author, isChatAvatar, isChatUserOnline} = props;

  if (author) {
    return <TeammateAvatar teammate={author} size={avatarSize} />;
  }

  // Otherwise, find the "from" recipient.
  if (!fromRecipient) {
    const error = new Error(`A message with no "from" recipient was provided (${props.message.id})`);
    reportToBugsnag(error);

    return <UnknownAvatar size={avatarSize} />;
  }
  return (
    <ContactAvatar
      visualContact={fromRecipient}
      size={avatarSize}
      isChatAvatar={isChatAvatar}
      isChatUserOnline={isChatUserOnline}
    />
  );
}

function renderContactDropdown(
  popoverProps: PopoverTransferProps,
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | AccountDetailsPanelId | ContactDetailsPanelId,
  fromRecipient: VisualRecipientModel,
) {
  return (
    <ContactCardWrapperContainer
      visualContact={fromRecipient}
      panelId={panelId}
      popoverProps={popoverProps}
      location={ContactCardLocationsEnum.MESSAGE_VIEWER}
    />
  );
}
